import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Upload, Image as ImageIcon } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import TranslateButton from '../../../components/admin/TranslateButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';
import ImageSuggestionModal from '../../../components/admin/ImageSuggestionModal';
import BunnyLibraryModal from '../../../components/admin/BunnyLibraryModal';
import ImageControlsPanel from '../../../components/admin/ImageControlsPanel';
import { uploadImage } from '../../../services/imageService';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CTAForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    heading_pt: '',
    heading_en: '',
    subheading_pt: '',
    subheading_en: '',
    trust_note_pt: '',
    trust_note_en: '',
    background_image: '',
    overlay_opacity: 40,
    image_brightness: 100,
    button_primary: {
      text_pt: '',
      text_en: '',
      link: ''
    },
    button_secondary: {
      text_pt: '',
      text_en: '',
      link: ''
    }
  });

  useEffect(() => {
    loadCTAData();
  }, []);

  const loadCTAData = async () => {
    setLoading(true);
    try {
      const [ptResponse, enResponse] = await Promise.all([
        axios.get(`${API_URL}/api/homepage-ml/cta?lang=pt`),
        axios.get(`${API_URL}/api/homepage-ml/cta?lang=en`)
      ]);

      const ptData = ptResponse.data;
      const enData = enResponse.data;

      setFormData({
        heading_pt: ptData.heading || '',
        heading_en: enData.heading || '',
        subheading_pt: ptData.subheading || '',
        subheading_en: enData.subheading || '',
        trust_note_pt: ptData.trust_note || '',
        trust_note_en: enData.trust_note || '',
        background_image: ptData.background_image || '',
        overlay_opacity: ptData.overlay_opacity != null ? ptData.overlay_opacity : 40,
        image_brightness: ptData.image_brightness != null ? ptData.image_brightness : 100,
        button_primary: ptData.button_primary || { text_pt: '', text_en: '', link: '' },
        button_secondary: ptData.button_secondary || { text_pt: '', text_en: '', link: '' }
      });
    } catch (error) {
      console.error('Failed to load CTA:', error);
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleButtonChange = (buttonType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [buttonType]: { ...prev[buttonType], [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/api/homepage-ml/cta`, formData);
      toast({ title: "Sucesso", description: "Secção CTA guardada" });
    } catch (error) {
      toast({ variant: "destructive", title: "Erro", description: "Falha ao guardar" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Secção CTA (Call-to-Action)</CardTitle>
            <CardDescription>Configure o call-to-action final da homepage</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Portuguese */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#00B4D8]">🇵🇹 Português</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Título Principal (PT)</label>
                    <input
                      type="text"
                      value={formData.heading_pt}
                      onChange={(e) => handleInputChange('heading_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Pronto para Transformar..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subtítulo (PT)</label>
                    <textarea
                      value={formData.subheading_pt}
                      onChange={(e) => handleInputChange('subheading_pt', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Agende uma reunião..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Nota de Confiança (PT)</label>
                    <input
                      type="text"
                      value={formData.trust_note_pt}
                      onChange={(e) => handleInputChange('trust_note_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Resposta em 24 horas"
                    />
                  </div>
                </div>
              </div>

              {/* English */}
              <div className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#00B4D8]">🇬🇧 English</h3>
                  <TranslateButton
                    sourceTexts={{
                      heading: formData.heading_pt,
                      subheading: formData.subheading_pt,
                      trust_note: formData.trust_note_pt
                    }}
                    onTranslated={(translated) => {
                      setFormData(prev => ({
                        ...prev,
                        heading_en: translated.heading || '',
                        subheading_en: translated.subheading || '',
                        trust_note_en: translated.trust_note || ''
                      }));
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Main Heading (EN)</label>
                    <input
                      type="text"
                      value={formData.heading_en}
                      onChange={(e) => handleInputChange('heading_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Ready to Transform..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subheading (EN)</label>
                    <textarea
                      value={formData.subheading_en}
                      onChange={(e) => handleInputChange('subheading_en', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Schedule a meeting..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Trust Note (EN)</label>
                    <input
                      type="text"
                      value={formData.trust_note_en}
                      onChange={(e) => handleInputChange('trust_note_en', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Response within 24 hours"
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Botões</h3>
                
                <div className="space-y-4">
                  <div className="border p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Botão Principal</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.button_primary.text_pt}
                        onChange={(e) => handleButtonChange('button_primary', 'text_pt', e.target.value)}
                        placeholder="Texto PT"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.button_primary.text_en}
                        onChange={(e) => handleButtonChange('button_primary', 'text_en', e.target.value)}
                        placeholder="Text EN"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.button_primary.link}
                        onChange={(e) => handleButtonChange('button_primary', 'link', e.target.value)}
                        placeholder="Link"
                        className="px-3 py-2 border rounded"
                      />
                    </div>
                  </div>

                  <div className="border p-4 rounded-lg">
                    <h4 className="font-medium mb-3">Botão Secundário</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.button_secondary.text_pt}
                        onChange={(e) => handleButtonChange('button_secondary', 'text_pt', e.target.value)}
                        placeholder="Texto PT"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.button_secondary.text_en}
                        onChange={(e) => handleButtonChange('button_secondary', 'text_en', e.target.value)}
                        placeholder="Text EN"
                        className="px-3 py-2 border rounded"
                      />
                      <input
                        type="text"
                        value={formData.button_secondary.link}
                        onChange={(e) => handleButtonChange('button_secondary', 'link', e.target.value)}
                        placeholder="Link"
                        className="px-3 py-2 border rounded"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Image Controls */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#2B2D42]">Background Image</h3>
                <ImageControlsPanel
                  imageUrl={formData.background_image}
                  overlayOpacity={formData.overlay_opacity}
                  imageBrightness={formData.image_brightness}
                  onImageUrlChange={(url) => setFormData(prev => ({ ...prev, background_image: url }))}
                  onOverlayChange={(val) => setFormData(prev => ({ ...prev, overlay_opacity: val }))}
                  onBrightnessChange={(val) => setFormData(prev => ({ ...prev, image_brightness: val }))}
                  onRemove={() => setFormData(prev => ({ ...prev, background_image: '' }))}
                  onSuggestionsClick={() => setSuggestionsOpen(true)}
                  onLibraryClick={() => setLibraryOpen(true)}
                  uploadInputId="cta-upload"
                  onFileUpload={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const result = await uploadImage(file, 'images/cta');
                      if (result.success) setFormData(prev => ({ ...prev, background_image: result.data.cdn_url }));
                    } catch (err) { console.error(err); }
                    setUploading(false);
                  }}
                  uploading={uploading}
                />
              </div>

              {/* Actions */}
              <div className="space-y-4 pt-6 border-t">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>Cancelar</Button>
                  <Button type="submit" disabled={saving} className="bg-[#00B4D8]">
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A guardar...</> : <><Save className="w-4 h-4 mr-2" />Guardar</>}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <ImageSuggestionModal category="cta" isOpen={suggestionsOpen} onClose={() => setSuggestionsOpen(false)}
          onSelect={(url) => { setFormData(prev => ({ ...prev, background_image: url })); setSuggestionsOpen(false); }} />
        <BunnyLibraryModal folder="cta" isOpen={libraryOpen} onClose={() => setLibraryOpen(false)}
          onSelect={(url) => { setFormData(prev => ({ ...prev, background_image: url })); setLibraryOpen(false); }} />
      </div>
    </div>
  );
};

export default CTAForm;